import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  CreditReportData,
  CreditReportType,
} from "@prescreen/lib/types/credit-report-client"
import { CreditEvaluation } from "@prescreen/lib/types/credit-evaluation"
import { EquifaxReportView } from "@/components/EquifaxReportView"
import { apiFetch } from "@prescreen/lib/api-client"

export default function CreditReportPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const reportId = id as string

  const [creditReport, setCreditReport] = useState<CreditReportData | null>(null)
  const [evaluations, setEvaluations] = useState<CreditEvaluation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  useEffect(() => {
    const loadCreditReport = async () => {
      if (!reportId) {
        setError("Missing report ID")
        setIsLoading(false)
        return
      }

      try {
        const response = await apiFetch(`/api/credit-reports/${reportId}`)
        const json = await response.json()

        if (json.success) {
          setCreditReport(json.data.creditReport)
          setEvaluations(json.data.evaluations || [])
        } else {
          setError(json.error || "Failed to load credit report")
        }
      } catch (err) {
        setError("Network error. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    loadCreditReport()
  }, [reportId])

  const getConsumerName = (report: CreditReportData): string => {
    try {
      if (
        report.reportType === CreditReportType.Prequal ||
        report.reportType === CreditReportType.PrequalCombined ||
        report.reportType === CreditReportType.PrequalCoborrower
      ) {
        const subjectName =
          report.report?.consumers?.equifaxUSConsumerCreditReport?.[0]
            ?.subjectName
        if (subjectName) {
          const parts = [
            subjectName.firstName,
            subjectName.middleName,
            subjectName.lastName,
          ].filter(Boolean)
          return parts.join(" ") || "Unknown"
        }
      }
      return "Unknown"
    } catch {
      return "Unknown"
    }
  }

  const handleGeneratePdf = async () => {
    if (!creditReport?._id) return

    setIsGeneratingPdf(true)
    try {
      const id = creditReport.prescreenRequestId || creditReport._id
      const creditReportId = creditReport._id

      const response = await apiFetch(
        `/api/credit-reports/${creditReportId}/pdf`
      )

      if (!response.ok) {
        throw new Error("Failed to generate PDF")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const newWindow = window.open(url, "_blank")

      if (newWindow) {
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 100)
      } else {
        const a = document.createElement("a")
        a.href = url
        a.download = `credit-report-${creditReportId}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error("Error generating PDF:", err)
      setError(err instanceof Error ? err.message : "Failed to generate PDF")
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading credit report...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !creditReport) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">
                Error Loading Credit Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertDescription>
                  {error || "Credit report not found"}
                </AlertDescription>
              </Alert>
              <div className="mt-4">
                <Button onClick={() => navigate("/reports")} variant="outline">
                  Back to Reports
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button onClick={() => navigate("/reports")} variant="outline">
              ← Back to Reports
            </Button>
            <Button
              onClick={handleGeneratePdf}
              disabled={isGeneratingPdf || !creditReport?._id}
              variant="default"
            >
              {isGeneratingPdf ? "Generating PDF..." : "Generate PDF"}
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Credit Report Details
          </h1>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Report Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Consumer Name
                  </h4>
                  <p className="text-sm">{getConsumerName(creditReport)}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Report Type
                  </h4>
                  <Badge variant="outline">{creditReport.reportType}</Badge>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Source
                  </h4>
                  <Badge variant="secondary">{creditReport.source}</Badge>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Report ID
                  </h4>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {creditReport._id}
                  </code>
                </div>
                {creditReport.prescreenRequestId && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-2">
                      Prequalification Request ID
                    </h4>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {creditReport.prescreenRequestId}
                    </code>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <EquifaxReportView
            rawReport={creditReport.report}
            evaluations={evaluations}
          />
        </div>
      </div>
    </div>
  )
}

